import { Suspense, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useHistory, useLocation, useParams } from 'react-router-dom';

import { Button, Card, Flex, Tabs } from 'antd';
import { Typography } from 'antd';

import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import utc from 'dayjs/plugin/utc';
import { v4 as uuid } from 'uuid';
import { v4 as uuidv4 } from 'uuid';

import axios from '../../axios';

import { buildPointFeature } from '../../helpers/map';

import { GEOMETRY_TYPE, MAPS_TAB } from '../../constants/map';
import messages from '../../constants/messages';
import { TABLE_CONFIG_KEYS } from '../../constants/table-configurations';

import { useColumns } from '../../hooks/useTrackLivestockColumns';
import { useFilterOptions } from '../../hooks/useTrackLivestockFilterOptions';

import { fetchAnimal, fetchAnimals } from '../../redux/actions/animal';
import { fetchFarms } from '../../redux/actions/farm';
import { fetchGrazingData, fetchGrazingHeatmapStyles } from '../../redux/actions/grazing';
import { fetchLabels } from '../../redux/actions/label';
import { setSortedData } from '../../redux/reducers/animal';
import { setSelectedFilters } from '../../redux/reducers/filters';

import AppTable from '../../components/AppTable';
import LoadingBar from '../../components/LoadingBar';
import Map from '../../components/Maps/MapTracking';
import PrevNextButtons from '../../components/PrevNextButtons';
import LivestockDetails from '../../components/TrackLivestock/LivestockDetails';
import TimeSliders from '../../components/TrackLivestock/TimeSliders';
import {
  MIN_TIME_FRAME_DOTS_NUMBER,
  SLIDER,
  SLIDER_DEFAULT_TIME_FRAME,
  SLIDER_DEFAULT_TIME_INTERVAL
} from '../../components/TrackLivestock/constants';
import { errorToastHandler, infoToastHandler } from '../../components/action_notifier';

import AppBreadcrumb from '../ui-components/AppBreadcrumb';

dayjs.extend(utc);
dayjs.extend(isBetween);

const DEFAULT_TAB = 'livestockLocation';
const { Title } = Typography;

export default function Tracking() {
  const history = useHistory();
  const dispatch = useDispatch();

  const { search } = useLocation();
  const { id } = useParams();
  const queryParams = new URLSearchParams(search);
  const tab = queryParams.get('tab');

  const { data: animals, animal, loading: animalsLoaded } = useSelector(state => state.animal);
  const { options: labels } = useSelector(state => state.label);
  const { list: farms } = useSelector(state => state.farm);
  const { selectedFilters } = useSelector(state => state.filters);
  const { data: grazing } = useSelector(state => state.grazing);

  const [isLoading, setIsLoading] = useState(true);
  const [filteredInfo, setFilteredInfo] = useState({});
  const [mapData, setMapData] = useState(null);
  const [state, setState] = useState({
    all_tracks: {},
    all_tracksCopy: {},
    animal_tracking: {},
    animal_tracking_filtered: {},
    endDateTime: dayjs.utc().format(),
    filters: {
      animal_ids: [],
      farms: [],
      labels: [],
      notifications: false,
      time: null
    },
    filter_open: false,
    isFilterApplied: false,
    time_frame: SLIDER_DEFAULT_TIME_FRAME,
    time_interval: SLIDER_DEFAULT_TIME_INTERVAL
  });

  useEffect(() => {
    resetFilters();

    async function fetchData() {
      try {
        await Promise.all([
          dispatch(fetchFarms({ with_details: true })),
          dispatch(fetchLabels()),
          dispatch(fetchGrazingHeatmapStyles()),
          getAnimals(),
          getAllTracks(),
          getGrazingData()
        ]);
      } catch (error) {
        errorToastHandler(messages.FAILED_ON_FETCH_DATA);
      } finally {
        setIsLoading(false);
      }
    }

    fetchData();

    return () => dispatch(setSelectedFilters({}));
  }, []);

  useEffect(() => {
    saveMapData();
  }, [farms]);

  useEffect(() => {
    getAnimal();
  }, [id]);

  useEffect(() => {
    if (state.filters.animal_ids || state.filters.farms) {
      showSelectedAnimals();
    }
  }, [state.filters.animal_ids, state.filters.farms]);

  function saveMapData() {
    if (!farms.length) return;

    const currentAnimalFarm = farms.find(farm => farm.id === animal?.farm_id);

    if (!currentAnimalFarm) return;

    const master_geofences = [currentAnimalFarm?.master_geofence];
    const geofences = currentAnimalFarm?.geofences?.filter(g => !g.is_master);
    const sites = currentAnimalFarm?.sites;

    setMapData({ farms: master_geofences, geofences, sites });
  }

  const actions = () => {
    return [
      {
        label: (
          <Button
            color="default"
            size="small"
            variant="link"
            disabled={!Object.values(selectedFilters)?.length}
            onClick={clearFilters}>
            Clear all filters
          </Button>
        )
      }
    ];
  };

  function toggleFilters() {
    setState(prevState => ({ ...prevState, filter_open: !state.filter_open }));
  }

  function closeFilters(bool) {
    setState(prevState => ({ ...prevState, filter_open: false, isFilterApplied: bool }));
  }

  async function getAllTracks() {
    try {
      const response = await axios.get('animals/tag-data', {
        params: {
          label_ids: labels?.map(x => x.value),
          only_notifications: state.filters.notifications,
          farm_ids: state.filters.farms.map(x => x.value)
        }
      });

      if (response.status === 200) {
        if (response.data.features) {
          response.data.features = response.data.features.map(x => {
            if (x.properties) x.properties['isVisible'] = true;

            return x;
          });
        }

        if (animalsLoaded && animals?.length === 0) {
          setState(prevState => ({
            ...prevState,
            tracksLoaded: true,
            all_tracks: {},
            all_tracksCopy: {}
          }));

          return;
        }

        response.data.features.map(m => {
          animals?.find(x => {
            if (x.id === m.properties.animal_id) {
              m.geometry.coordinates[0] = x?.tag?.lat;
              m.geometry.coordinates[1] = x?.tag?.lng;
            }
          });
        });

        const res = animals?.filter(
          item1 => !response.data.features.some(item2 => item2.properties.animal_id === item1.id)
        );

        const arr = [];

        for (const i in res) {
          arr.push(buildPointFeature(res[i]));
        }

        response.data.features = response.data.features.concat(arr);

        setState(prevState => ({
          ...prevState,
          tracksLoaded: true,
          all_tracks: response.data,
          all_tracksCopy: response.data
        }));

        showSelectedAnimals();
      }
    } catch (e) {
      setState(prevState => ({ ...prevState, tracksLoaded: true }));
    }
  }

  async function getAnimal() {
    if (!id) return;

    try {
      const response = await dispatch(fetchAnimal({ id, params: { only_linked: true } })).unwrap();

      const animalTagData = await axios.get(`animals/${id}/tag-data`, {
        params: {
          from_date: dayjs.utc().subtract(48, 'hour').format(), // LAST 2 DAYS DATA (24*2)
          only_notifications: state.filters.notifications
        }
      });

      if (animalTagData.data.features) {
        animalTagData.data.features = animalTagData.data.features.map(item => {
          return {
            ...item,
            properties: {
              ...item.properties,
              eartag_management_id: response?.eartag_management_id,
              eartag_official_id: response?.eartag_official_id,
              hasAlerts: response?.hasAlerts,
              identifier: response?.identifier,
              isVisible: true,
              uniqueId: uuidv4()
            }
          };
        });
      }

      setState(prevState => ({
        ...prevState,
        tracksLoaded: true,
        animal_tracking: animalTagData.data,
        farm: state.farms?.find(x => x.id === response?.farm_id)
      }));
      // FILTER TRACKING DATA ACCORDING TO SLIDERS //
      filterAnimalTracking(animalTagData.data);
    } catch (e) {
      setState(prevState => ({ ...prevState, tracksLoaded: true }));
    }
  }

  async function getAnimals() {
    try {
      await dispatch(
        fetchAnimals({
          only_linked: true,
          label_ids: labels?.map(x => x.value),
          only_notifications: state.filters.notifications,
          farm_ids: state.filters.farms.map(x => x.value)
        })
      ).unwrap();
    } catch (error) {
      errorToastHandler(error?.response?.data?.message || messages.FAILED_ON_FETCH_DATA);
    }
  }

  async function getGrazingData() {
    if (!farms.length) return;

    const currentAnimalFarm = farms.find(farm => farm.id === animal?.farm_id);

    if (!currentAnimalFarm) return;

    const geofences = currentAnimalFarm?.geofences?.filter(g => !g.is_master);
    const rotationIds = geofences?.map(g => (g.currentRotation ? g.currentRotation.id : g.lastRotation?.id));

    if (!rotationIds?.length) return;

    try {
      await dispatch(fetchGrazingData({ rotationIds })).unwrap();
    } catch (error) {
      errorToastHandler(error?.message || error?.response?.data?.message || messages.FAILED_ON_FETCH_DATA);
    }
  }

  function setAnimal(id) {
    history.push(`/tracking/${id}`);
  }

  function resetFilters(isClose) {
    setState(prevState => ({
      ...prevState,
      filters: {
        ...prevState.filters,
        notifications: false,
        animal_ids: [],
        farms: []
      }
    }));

    updateFilters(isClose);
  }

  const updateFilters = bool => {
    setState(state => ({
      ...state,
      filters: {
        ...state.filters,
        animal_ids: []
      }
    }));
    getAnimals();
    showSelectedAnimals();
    if (bool) {
      closeFilters(true);
      return;
    }
    closeFilters(bool);
  };

  const displayFarmsOnMap = useCallback(async (pagination, filters, sorter, extra) => {
    const farms = extra.currentDataSource.map(c => ({ label: c?.farm?.name, value: c?.farm?.id }));

    if (extra.action === 'filter') {
      setState(state => ({
        ...state,
        filters: {
          ...state.filters,
          animal_ids: [],
          farms
        }
      }));

      try {
        await getAnimals();
      } catch (error) {
        errorToastHandler(error?.response?.data?.message || messages.DEFAULT_ERROR);
      }
    }
  }, []);

  const handleTableChange = useCallback(
    (pagination, filters, sorter, extra) => {
      displayFarmsOnMap(pagination, filters, sorter, extra);

      setFilteredInfo(filters);

      dispatch(setSelectedFilters(filters));
      dispatch(setSortedData(extra.currentDataSource));
    },
    [displayFarmsOnMap, dispatch]
  );

  function showSelectedAnimals() {
    if (state.all_tracksCopy && state.all_tracksCopy.features) {
      let filteredFeatures = [...state.all_tracksCopy.features];
      const filterFarms = state.filters.farms?.map(({ value }) => value);
      const filterAnimals = state.filters.animal_ids;

      if (filterFarms.length) {
        filteredFeatures = filteredFeatures.filter(item => filterFarms.includes(item.properties.farm_id));
      }

      if (filterAnimals.length) {
        filteredFeatures = filteredFeatures.filter(item => filterAnimals.includes(item.properties.animal_id));
      }

      setState(prevState => ({
        ...prevState,
        all_tracks: {
          ...prevState.all_tracks,
          features: filteredFeatures
        }
      }));
    }
  }

  function sliderHandler(value, field) {
    state[field] = value;

    filterAnimalTracking(state.animal_tracking);
  }

  function filterAnimalTracking(animal_tracking) {
    const time_frame = SLIDER.timeFrameSlots.filter(x => x.index === state.time_frame)?.at(0);
    const time_interval = SLIDER.timeIntervalSlots.filter(x => x.index === state.time_interval)?.at(0);

    let line_coordinates = [];
    let animal_tracking_filtered = [];

    const last_reading = animal_tracking.features
      .filter(x => x.geometry.type === GEOMETRY_TYPE.POINT)
      .filter(x => x.properties.last_reading);

    if (animal_tracking.type) {
      animal_tracking_filtered = animal_tracking.features
        .filter(x => x.geometry.type === GEOMETRY_TYPE.POINT)
        .filter(x => !x.properties.last_reading)
        .map(x => {
          delete x.properties.customIcon;
          return x;
        });

      // APPLY FILTERS 'TIME FRAME & TIME INTERVAL' //
      if (time_frame.index !== 100 || time_interval.index !== 0) {
        // ADDED 12 MINS MORE, SO CURRENT/START POINT CAN ALSO CONSIDER //
        const startDateTime = dayjs(state.endDateTime)
          .utc()
          .subtract(time_frame.value, 'hour')
          .subtract(12, 'minute')
          .format();

        // FILTER WITH 'TIME FRAME' //
        if (time_frame.index !== 100) {
          animal_tracking_filtered = animal_tracking_filtered.filter(item =>
            dayjs(item.properties.time).isBetween(startDateTime, state.endDateTime)
          );
        }
        // FILTER WITH 'TIME INTERVAL'; GET EVERY NTH ELEMENT; //
        if (time_interval.index !== 0) {
          let temp_ary = [];
          for (let i = 0; i < animal_tracking_filtered.length; i = i + time_interval.value) {
            temp_ary.push(animal_tracking_filtered[i]);
          }
          animal_tracking_filtered = temp_ary;
        }
      }
      line_coordinates = animal_tracking_filtered.map(x => x.geometry.coordinates);
    }

    const isLastReading = !animal_tracking_filtered.length;

    const isPartialReading =
      animal_tracking_filtered.length &&
      animal_tracking_filtered.length <= MIN_TIME_FRAME_DOTS_NUMBER[time_frame.value];

    if (animal_tracking_filtered.length > 1) {
      // MINIMUM 2 COORDINATES REQUIRED FOR LINE //
      animal_tracking_filtered.unshift({
        type: 'Feature',
        geometry: {
          type: GEOMETRY_TYPE.LINE,
          coordinates: line_coordinates
        },
        properties: { uniqueId: uuid() }
      });
    }

    if (isLastReading) {
      animal_tracking_filtered = last_reading;
      infoToastHandler(messages.NO_LOCATION_DATA_AVAILABLE_FOR_TIME_FRAME);
    }

    if (isPartialReading) {
      infoToastHandler(messages.PARTIAL_LOCATION_DATA_AVAILABLE_FOR_PART_TIME);
    }

    setState(prevState => ({
      ...prevState,
      animal_tracking_filtered: {
        type: state.animal_tracking.type,
        features: animal_tracking_filtered
      }
    }));
  }

  const selectRecords = checkedIds => {
    setState(prevState => ({
      ...prevState,
      filters: {
        ...prevState.filters,
        animal_ids: checkedIds
      }
    }));
  };

  const columnFilterOptions = useFilterOptions({ animals });
  const columns = useColumns({ filteredInfo, columnFilterOptions });

  const clearFilters = () => {
    setFilteredInfo({});
    dispatch(setSelectedFilters({}));
  };

  const items = [
    {
      key: 'livestockLocation',
      label: 'Livestock Location',
      children: (
        <Suspense fallback={<LoadingBar />}>
          <AppTable
            headerClass="py-2"
            actions={actions}
            baseColumns={columns}
            dataSource={animals}
            filterable={false}
            loading={isLoading}
            searchable={false}
            tableConfigKey={TABLE_CONFIG_KEYS.TRACK_LIVESTOCK}
            handleFilters={toggleFilters}
            handleOnChange={handleTableChange}
            handleOnSelectRecords={selectRecords}
          />
        </Suspense>
      )
    }
  ];

  const breadcrumbItems = [
    {
      title: <Link to={`/maps?tab=${MAPS_TAB.LIVESTOCK}`}>Track Livestock</Link>
    }
  ];

  if (animal) {
    breadcrumbItems.push({
      title: <Link to={`/animals/${animal.id}`}>Livestock {animal.identifier || animal.eartag_official_id}</Link>
    });
  }

  return (
    <>
      {isLoading ? (
        <LoadingBar fullscreen={true} />
      ) : (
        <div key={id}>
          <Flex gap="small" vertical={false} justify="space-between" wrap>
            <Flex vertical={true}>
              <Title level={4}>Track Livestock {animal && (animal.identifier || animal.eartag_official_id)}</Title>

              <AppBreadcrumb items={breadcrumbItems} />
            </Flex>
            <Flex gap="small">
              {animal && (
                <PrevNextButtons
                  className="float-right"
                  nextId={animal?.next_id}
                  prevId={animal?.prev_id}
                  handlePrev={() => history.push(`/tracking/${animal.prev_id}`)}
                  handleNext={() => history.push(`/tracking/${animal.next_id}`)}
                />
              )}
            </Flex>
          </Flex>

          <Map
            animal={animal}
            grazing={grazing}
            tracking={animal ? state.animal_tracking_filtered : state.all_tracks}
            mapData={mapData}
            onAnimalClick={setAnimal}
          />

          {animal && <TimeSliders sliderHandler={sliderHandler} />}

          {animal && <LivestockDetails animal={animal} />}

          <Card>
            <Tabs defaultActiveKey={tab || DEFAULT_TAB} items={items} />
          </Card>
        </div>
      )}
    </>
  );
}
